import { ReactElement } from 'react';
import { QueryStatus } from '@tanstack/react-query';
import ErrorComponent from '@pages/_error';
import { ApiError } from '@services/api/ApiService';
import { ApiStatus } from '@types';
import { convertRQueryPropsToApiStatus } from '@utils';

export interface RequestedDataRendererProps {
	/** Component to render when there is any data on api response */
	fulfilledComponent: ReactElement;
	/** Component to render when there is not any data on api response */
	emptyComponent: ReactElement;
	/** Component to render meanwhile api response is pending */
	loaderComponent: ReactElement;
	/** Component to render when there is an error on api response */
	errorComponent?: ReactElement;
	/**
	 * Api request status.
	 * It can be a single status or a set of requests statuses. If it is a set, final status
	 * will be considered as 'success' when all of them are successful
	 */
	queryStatus: QueryStatus | QueryStatus[];
	/**
	 * Data as object or list to check if it is empty.
	 * If so, empty component will be rendered.
	 */
	data: unknown;
	/**
	 * An error component will be rendered according to error status code on api response.
	 * Optional. It renders a component as default
	 */
	error?: ApiError | unknown;
}

/**
 * According to api status, render components for waiting api response, fulfilled data, empty data or error on api response
 * @param RequestedDataRendererProps
 * @returns
 */
export const RequestedDataRenderer = ({
	fulfilledComponent,
	emptyComponent,
	loaderComponent,
	errorComponent,
	queryStatus,
	data,
	error,
}: RequestedDataRendererProps) => {
	const errorStatusCode = getErrorStatusCode(error);
	const defaultErrorComponent = <ErrorComponent statusCode={errorStatusCode} />;
	const getErrorComponent = () => (errorComponent ? errorComponent : defaultErrorComponent);

	const isLoading = Array.isArray(queryStatus)
		? queryStatus.some(status => status == 'pending')
		: queryStatus == 'pending';
	const isSuccess = Array.isArray(queryStatus)
		? queryStatus.every(status => status == 'success')
		: queryStatus == 'success';
	const isError = Array.isArray(queryStatus)
		? queryStatus.some(status => status == 'error')
		: queryStatus == 'error';

	const apiStatus = convertRQueryPropsToApiStatus(isLoading, isSuccess, isError, data, error);

	return (() => {
		switch (apiStatus) {
			case ApiStatus.IDLE:
			case ApiStatus.PENDING:
				return loaderComponent;
			case ApiStatus.FULFILLED:
				return fulfilledComponent;
			case ApiStatus.EMPTY:
				return emptyComponent;
			case ApiStatus.ERROR:
				return getErrorComponent();
			default:
				return <></>;
		}
	})();
};

function getErrorStatusCode(error: ApiError | unknown) {
	return (error as ApiError)?.response?.status;
}
